"use client";

import { c as _c } from "react/compiler-runtime";
import { keepPreviousData, useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { updateAttachmentInCachedDrops, updateDropInCachedDrops } from "@/components/react-query-wrapper/utils/updateAttachmentInCachedDrops";
import type { ApiAttachment } from "@/generated/models/ApiAttachment";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { ApiDropType } from "@/generated/models/ApiDropType";
import { DropSize, type ExtendedDrop } from "@/helpers/waves/drop.helpers";
import type { WsDropUpdateMessage } from "@/helpers/Types";
import { WsMessageType } from "@/helpers/Types";
import { commonApiFetch } from "@/services/api/common-api";
import { useWebSocketMessage } from "@/services/websocket/useWebSocketMessage";
import { useDebouncedQueryRefetch } from "./useDebouncedQueryRefetch";
const DEFAULT_WAVE_DROPS_LIMIT = 20;
interface UseWaveDropsProps {
  readonly waveId: string;
  readonly dropType?: ApiDropType | undefined;
  readonly containsMedia?: boolean | undefined;
  readonly curationId?: string | undefined;
  readonly limit?: number | undefined;
  readonly enabled?: boolean | undefined;
}
const processDrops = (pages: ApiDrop[][] | undefined): ExtendedDrop[] => {
  if (!pages) {
    return [];
  }
  const allDrops = pages.flat();
  const extendedDrops: ExtendedDrop[] = allDrops.map(drop => ({
    ...drop,
    type: DropSize.FULL,
    stableKey: drop.id,
    stableHash: drop.id
  }));
  const keyCount = new Map<string, number>();
  return extendedDrops.map(drop => {
    const count = (keyCount.get(drop.stableKey) ?? 0) + 1;
    keyCount.set(drop.stableKey, count);
    if (count > 1) {
      return {
        ...drop,
        stableKey: `${drop.stableKey}-${count}`
      };
    }
    return drop;
  });
};
export function useWaveDrops(t0) {
  const $ = _c(43);
  const {
    waveId,
    dropType,
    containsMedia: t1,
    curationId,
    limit: t2,
    enabled: t3
  } = t0;
  const containsMedia = t1 === undefined ? false : t1;
  const limit = t2 === undefined ? DEFAULT_WAVE_DROPS_LIMIT : t2;
  const enabled = t3 === undefined ? true : t3;
  const t4 = dropType ?? null;
  const t5 = curationId ?? null;
  let t6;
  if ($[0] !== containsMedia || $[1] !== limit || $[2] !== t4 || $[3] !== t5 || $[4] !== waveId) {
    t6 = [QueryKey.DROPS, {
      waveId,
      limit,
      dropType: t4,
      containsMedia,
      curationId: t5,
      context: "wave-drops"
    }];
    $[0] = containsMedia;
    $[1] = limit;
    $[2] = t4;
    $[3] = t5;
    $[4] = waveId;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const queryKey = t6 as const;
  const queryClient = useQueryClient();
  let t7;
  if ($[6] !== containsMedia || $[7] !== curationId || $[8] !== dropType || $[9] !== limit || $[10] !== waveId) {
    t7 = async t8 => {
      const {
        pageParam
      } = t8;
      const params = {
        wave_id: waveId,
        limit: limit.toString()
      };
      if (containsMedia) {
        params.contains_media = "true";
      }
      if (dropType !== undefined) {
        params.drop_type = dropType;
      }
      if (curationId) {
        params.curation_id = curationId;
      }
      if (typeof pageParam === "number") {
        params.serial_no_less_than = `${pageParam}`;
      }
      return await commonApiFetch({
        endpoint: "drops",
        params
      });
    };
    $[6] = containsMedia;
    $[7] = curationId;
    $[8] = dropType;
    $[9] = limit;
    $[10] = waveId;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const t8 = enabled && !!waveId;
  let t9;
  if ($[12] !== queryKey || $[13] !== t7 || $[14] !== t8) {
    t9 = {
      queryKey,
      queryFn: t7,
      enabled: t8,
      initialPageParam: null,
      getNextPageParam: _temp,
      placeholderData: keepPreviousData,
      staleTime: 60000,
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      refetchOnReconnect: true
    };
    $[12] = queryKey;
    $[13] = t7;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const {
    data,
    dataUpdatedAt,
    error,
    fetchNextPage: onFetchNextPage,
    hasNextPage,
    isError,
    isFetching,
    isFetchingNextPage,
    isPlaceholderData,
    refetch
  } = useInfiniteQuery(t9);
  let t10;
  if ($[16] !== hasNextPage || $[17] !== isFetchingNextPage || $[18] !== onFetchNextPage) {
    t10 = async () => {
      if (hasNextPage && !isFetchingNextPage) {
        await onFetchNextPage();
      }
    };
    $[16] = hasNextPage;
    $[17] = isFetchingNextPage;
    $[18] = onFetchNextPage;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const fetchNextPage = t10;
  data?.pages;
  const t11 = data?.pages;
  let t12;
  if ($[20] !== t11) {
    t12 = processDrops(t11);
    $[20] = t11;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  const drops = t12;
  let t13;
  if ($[22] !== isFetching || $[23] !== isFetchingNextPage || $[24] !== refetch) {
    t13 = {
      refetch,
      isFetching,
      isFetchingNextPage
    };
    $[22] = isFetching;
    $[23] = isFetchingNextPage;
    $[24] = refetch;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  const requestRefetch = useDebouncedQueryRefetch(t13);
  let t14;
  if ($[26] !== queryClient || $[27] !== requestRefetch || $[28] !== waveId) {
    t14 = message => {
      if (waveId !== message.wave.id) {
        return;
      }
      updateDropInCachedDrops(queryClient, message);
      requestRefetch();
    };
    $[26] = queryClient;
    $[27] = requestRefetch;
    $[28] = waveId;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  useWebSocketMessage(WsMessageType.DROP_UPDATE, t14);
  let t15;
  if ($[30] !== queryClient) {
    t15 = attachment => {
      updateAttachmentInCachedDrops(queryClient, attachment);
    };
    $[30] = queryClient;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  useWebSocketMessage(WsMessageType.ATTACHMENT_STATUS_UPDATE, t15);
  let t16;
  if ($[32] !== dataUpdatedAt || $[33] !== drops || $[34] !== error || $[35] !== fetchNextPage || $[36] !== hasNextPage || $[37] !== isError || $[38] !== isFetching || $[39] !== isFetchingNextPage || $[40] !== isPlaceholderData || $[41] !== refetch) {
    t16 = {
      dataUpdatedAt,
      drops,
      error,
      fetchNextPage,
      hasNextPage,
      isError,
      isFetching,
      isFetchingNextPage,
      isPlaceholderData,
      refetch
    };
    $[32] = dataUpdatedAt;
    $[33] = drops;
    $[34] = error;
    $[35] = fetchNextPage;
    $[36] = hasNextPage;
    $[37] = isError;
    $[38] = isFetching;
    $[39] = isFetchingNextPage;
    $[40] = isPlaceholderData;
    $[41] = refetch;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  return t16;
}
function _temp(lastPage) {
  return lastPage.at(-1)?.serial_no ?? undefined;
}